import React, { useState, useEffect } from "react";
import { Plus, Search, RefreshCw, ChevronDown, ChevronUp, MessageSquare, X } from "lucide-react";
import { FaWrench, FaBolt, FaBug, FaPaintBrush, FaTools, FaCog } from "react-icons/fa";
import { MdElevator, MdPlumbing, MdElectricalServices } from "react-icons/md";
import Modal from './Modal'
import { API_BASE } from '../config'

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  return resp.json();
}

const CATEGORIES = [
  { value: "plumbing", label: "Plumbing", icon: MdPlumbing },
  { value: "electrical", label: "Electrical", icon: MdElectricalServices },
  { value: "carpentry", label: "Carpentry", icon: FaTools },
  { value: "painting", label: "Painting", icon: FaPaintBrush },
  { value: "pest_control", label: "Pest Control", icon: FaBug },
  { value: "elevator", label: "Elevator", icon: MdElevator },
  { value: "general", label: "General", icon: FaCog },
  { value: "other", label: "Other", icon: FaWrench },
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "bg-gray-100 text-gray-700" },
  { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-700" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-700" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-700" },
];

const STATUS_BADGES = {
  open: "bg-blue-100 text-blue-800",
  assigned: "bg-blue-100 text-blue-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  resolved: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-600",
  reopened: "bg-red-100 text-red-800",
};

function formatDate(val) {
  if (!val) return "";
  try {
    const d = new Date(val);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return val; }
}

function getCategoryIcon(cat) {
  const found = CATEGORIES.find(c => c.value === cat);
  if (found) {
    const Icon = found.icon;
    return <Icon size={16} />;
  }
  return <FaWrench size={16} />;
}

export default function ResidentIssues() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [toasts, setToasts] = useState([]);

  const communityId = sessionStorage.getItem("community_id") || "";
  const unitNbr = sessionStorage.getItem("unit_nbr") || "";
  const userEmail = (() => {
    try {
      const t = sessionStorage.getItem("id_token");
      if (!t) return "";
      const p = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      return (p.email || "").toLowerCase();
    } catch { return ""; }
  })();
  const userName = (() => {
    try {
      const t = sessionStorage.getItem("id_token");
      if (!t) return "";
      const p = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      return p.name || p.given_name || userEmail.split("@")[0] || "";
    } catch { return ""; }
  })();

  function showToast(message, type = "success") {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 3500);
  }

  async function loadIssues() {
    if (!communityId || !unitNbr) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/issues?communityId=${encodeURIComponent(communityId)}&unitNbr=${encodeURIComponent(unitNbr)}`);
      setIssues(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadIssues error:", err);
      showToast("Failed to load issues", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadIssues(); }, [communityId, unitNbr]);

  // ── New Issue Form ──
  const [form, setForm] = useState({ title: "", description: "", category: "general", priority: "medium" });
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.category) {
      showToast("Title and category are required", "error");
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch("/issues", {
        method: "POST",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          UNIT_NBR: unitNbr,
          TITLE: form.title.trim(),
          DESCRIPTION: form.description.trim(),
          CATEGORY: form.category,
          PRIORITY: form.priority,
          REPORTED_BY: userEmail,
          REPORTED_BY_NAME: userName,
        }),
      });
      showToast("Issue reported successfully!");
      setForm({ title: "", description: "", category: "general", priority: "medium" });
      setShowForm(false);
      loadIssues();
    } catch (err) {
      console.error("submit issue error:", err);
      showToast("Failed to submit issue", "error");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Filtering ──
  const filtered = issues.filter(issue => {
    const matchSearch = (issue.TITLE || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.CATEGORY || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === "all" || (issue.STATUS || "") === filterStatus;
    return matchSearch && matchStatus;
  });

  const statusCounts = {
    all: issues.length,
    open: issues.filter(i => i.STATUS === "open").length,
    assigned: issues.filter(i => i.STATUS === "assigned").length,
    in_progress: issues.filter(i => i.STATUS === "in_progress").length,
    resolved: issues.filter(i => i.STATUS === "resolved").length,
    closed: issues.filter(i => i.STATUS === "closed").length,
  };

  return (
    <div className="p-4">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] space-y-2">
        {toasts.map(t => (
          <div key={t.id} className={`px-4 py-2 rounded-lg shadow-lg text-white text-sm ${t.type === "error" ? "bg-red-500" : "bg-green-500"}`}>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="text-lg font-bold">Issues & Service Requests</h2>
        <button onClick={loadIssues} className="p-2 rounded-lg bg-gray-100 active:bg-gray-200">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Report Issue — full-width button */}
      <button
        onClick={() => setShowForm(true)}
        className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 mb-3 active:bg-blue-700"
      >
        <Plus size={18} /> Report Issue
      </button>

      {/* Report Issue Modal */}
      {showForm && (
        <Modal title="Report a New Issue" onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Leaking faucet in kitchen"
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Describe the issue in detail..."
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                >
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={form.priority}
                  onChange={e => setForm({ ...form, priority: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                >
                  {PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-50">
                {submitting ? "Submitting..." : "Submit Issue"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Search */}
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search issues..."
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
        />
      </div>

      {/* Status Filter Pills */}
      <div className="filter-pills pb-3">
        {[
          { key: "all", label: "All" },
          { key: "open", label: "Open" },
          { key: "assigned", label: "Assigned" },
          { key: "in_progress", label: "In Progress" },
          { key: "resolved", label: "Resolved" },
          { key: "closed", label: "Closed" },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setFilterStatus(s.key)}
            className={`filter-pill px-2.5 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs leading-tight font-medium text-center ${
              filterStatus === s.key ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
            }`}
          >
            {s.label} ({statusCounts[s.key] || 0})
          </button>
        ))}
      </div>

      {/* Issues List */}
      {loading && issues.length === 0 ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FaWrench size={28} className="mx-auto mb-2 text-gray-300" />
          <p className="text-center text-s text-white py-8m">No issues found</p>
          <p className="text-xs mt-1 text-white">Report an issue to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(issue => {
            const isExpanded = expandedId === issue.ISSUE_ID;
            const comments = Array.isArray(issue.COMMENTS) ? issue.COMMENTS : [];
            const priorityConfig = PRIORITIES.find(p => p.value === issue.PRIORITY) || PRIORITIES[1];

            return (
              <div key={issue.ISSUE_ID} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full p-4 text-left"
                  onClick={() => setExpandedId(isExpanded ? null : issue.ISSUE_ID)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="mt-0.5 text-gray-500">{getCategoryIcon(issue.CATEGORY)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{issue.TITLE}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_BADGES[issue.STATUS] || STATUS_BADGES.open}`}>
                            {(issue.STATUS || "open").replace("_", " ").toUpperCase()}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${priorityConfig.color}`}>
                            {priorityConfig.label}
                          </span>
                          <span className="text-[10px] text-gray-400">{formatDate(issue.CREATED_AT)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {comments.length > 0 && (
                        <span className="flex items-center gap-1 text-gray-400 text-xs">
                          <MessageSquare size={12} /> {comments.length}
                        </span>
                      )}
                      {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 pb-4">
                    {issue.DESCRIPTION && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                        <p className="text-sm text-gray-700">{issue.DESCRIPTION}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                      <div>
                        <p className="text-gray-500">Category</p>
                        <p className="font-medium capitalize">{(issue.CATEGORY || "").replace("_", " ")}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Unit</p>
                        <p className="font-medium">{issue.UNIT_NBR}</p>
                      </div>
                      {issue.ASSIGNED_TO_NAME && (
                        <div>
                          <p className="text-gray-500">Assigned To</p>
                          <p className="font-medium">{issue.ASSIGNED_TO_NAME}</p>
                        </div>
                      )}
                      {issue.RESOLVED_AT && (
                        <div>
                          <p className="text-gray-500">Resolved</p>
                          <p className="font-medium">{formatDate(issue.RESOLVED_AT)}</p>
                        </div>
                      )}
                    </div>

                    {/* Comments */}
                    {comments.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 mb-2">Comments ({comments.length})</p>
                        <div className="space-y-2">
                          {comments.map((cmt, idx) => (
                            <div key={cmt.COMMENT_ID || idx} className="bg-gray-50 rounded-lg p-2">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-gray-700">{cmt.AUTHOR_NAME || cmt.AUTHOR || "Staff"}</span>
                                <span className="text-[10px] text-gray-400">{formatDate(cmt.CREATED_AT)}</span>
                              </div>
                              <p className="text-xs text-gray-600">{cmt.TEXT}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
